import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  X, Bell, ArrowsClockwise, CheckCircle, Clock, PencilSimple, TrashSimple, Plus,
  PaperPlaneTilt, ArrowRight, Lightning, Package, UsersThree, Broadcast, Eye,
  Question, Cpu,
} from "@phosphor-icons/react";
import AlertFlowHelp from "./AlertFlowHelp";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EVENT_LABELS = {
  temperature_excursion: "Temperature Excursion",
  seal_breach: "Seal Breach",
  reefer_failure: "Reefer Failure",
  delayed_delivery: "Delayed Delivery",
  missing_signature: "Missing Signature",
  device_offline: "Device Offline",
  corrective_action_required: "Corrective Action Required",
  corrective_action_overdue: "Corrective Action Overdue",
  corrective_action_escalated: "Corrective Action Escalated",
};

const CHANNEL_LABELS = {
  in_app: "In-app",
  email:  "Email",
  sms:    "SMS",
  slack:  "Slack",
};

const ROLE_LABELS = {
  shipper: "Shipper", consignee: "Consignee", receiver: "Receiver",
  carrier: "Carrier", warehouse: "Warehouse", qa_manager: "QA Manager",
  compliance_contact: "Compliance Contact", monitored_by: "Monitored By", approved_by: "Approved By",
};

const SEVERITY_COLORS = {
  info: "text-blue-300 bg-blue-500/10 border-blue-700/40",
  warning: "text-amber-300 bg-amber-500/10 border-amber-700/40",
  critical: "text-red-300 bg-red-500/10 border-red-700/40",
};

export default function NotificationRulesManager({ open, onClose }) {
  const [rules, setRules] = useState([]);
  const [log, setLog] = useState([]);
  const [enums, setEnums] = useState(null);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null);
  const [activeTab, setActiveTab] = useState("rules"); // rules | log
  const [helpOpen, setHelpOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [r, l, e] = await Promise.all([
        axios.get(`${API}/notification-rules`),
        axios.get(`${API}/notification-rules/log?limit=50`),
        enums ? Promise.resolve({ data: enums }) : axios.get(`${API}/notification-rules/meta/enums`),
      ]);
      setRules(r.data.items || []);
      setLog(l.data.items || []);
      if (!enums) setEnums(e.data);
    } catch {
      toast.error("Failed to load routing data");
    } finally {
      setLoading(false);
    }
  }, [enums]);

  useEffect(() => { if (open) load(); }, [open, load]);

  if (!open) return null;

  const seedDefaults = async () => {
    try {
      const { data } = await axios.post(`${API}/notification-rules/seed-defaults`);
      toast.success(`${data.seeded} default rules added`);
      load();
    } catch {
      toast.error("Seed failed");
    }
  };

  const deleteRule = async (id, evType) => {
    if (!window.confirm(`Delete the ${EVENT_LABELS[evType] || evType} rule?`)) return;
    try {
      await axios.delete(`${API}/notification-rules/${id}`);
      toast.success("Rule deleted");
      load();
    } catch {
      toast.error("Delete failed");
    }
  };

  const ackLog = async (id) => {
    try {
      await axios.post(`${API}/notification-rules/log/${id}/ack`);
      toast.success("Acknowledged");
      load();
    } catch {
      toast.error("Ack failed");
    }
  };

  return (
    <div className="fixed inset-0 z-[1900] bg-black/70 backdrop-blur-sm flex items-stretch justify-center" onClick={onClose}>
      <div className="bg-zinc-950 border-l border-zinc-800 w-full max-w-5xl ml-auto flex flex-col" onClick={(e) => e.stopPropagation()}
        data-testid="notif-rules-manager">
        {/* Header */}
        <div className="px-6 py-4 border-b border-zinc-800 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-zinc-100 flex items-center gap-2">
              <Broadcast size={18} className="text-teal-400" weight="duotone" />
              Event Orchestration
              <span
                className="inline-flex items-center gap-1 text-[9px] uppercase tracking-wider font-bold text-teal-300 border border-teal-700/50 bg-teal-500/10 rounded px-1.5 py-0.5 ml-1"
                title="Rules engine that creates alerts, resolves contacts, dispatches channels, and manages SLA escalation."
                data-testid="orch-role-badge"
              >
                <Cpu size={9} weight="duotone" /> brain · rules engine
              </span>
            </h2>
            <p className="text-[11px] text-zinc-500 mt-0.5 max-w-2xl leading-relaxed">
              Creates alerts, resolves <span className="text-zinc-300">shipment-party roles</span> to
              contacts, dispatches <span className="text-zinc-300">channels</span>, and runs the{" "}
              <span className="text-zinc-300">SLA / escalation</span> timer. Operators handle the alerts
              over in <span className="text-amber-300">Alert Center</span>.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setHelpOpen(true)}
              className="h-8 text-[11px] border-teal-700/50 text-teal-300 hover:bg-teal-500/10"
              data-testid="orch-flow-help-btn"
            >
              <Question size={12} className="mr-1" weight="duotone" /> How alerts flow
            </Button>
            <Button variant="ghost" size="sm" onClick={load} className="h-8 w-8 p-0">
              <ArrowsClockwise size={14} />
            </Button>
            <Button variant="ghost" size="sm" onClick={onClose} className="h-8 w-8 p-0">
              <X size={16} />
            </Button>
          </div>
        </div>

        {/* Runtime model flow diagram */}
        <div className="px-6 py-3 bg-zinc-900/40 border-b border-zinc-800" data-testid="runtime-flow">
          <div className="flex items-center justify-between flex-wrap gap-2 text-[10px]">
            {[
              { icon: Lightning, label: "Event", sub: "telemetry / CA / AI",  tone: "text-amber-300" },
              { icon: Package,   label: "Shipment", sub: "context",             tone: "text-zinc-300" },
              { icon: UsersThree,label: "Roles",    sub: "QA, carrier, …",      tone: "text-sky-300" },
              { icon: Bell,      label: "Contacts", sub: "resolved at runtime", tone: "text-teal-300" },
              { icon: Broadcast, label: "Channels", sub: "in-app / email / sms",tone: "text-violet-300" },
              { icon: Clock,     label: "SLA",      sub: "escalate if no ack",  tone: "text-red-300" },
            ].map((step, i, arr) => {
              const Icon = step.icon;
              return (
                <div key={step.label} className="flex items-center gap-1.5">
                  <div className="flex items-center gap-1.5 px-2 py-1 rounded border border-zinc-800 bg-zinc-950/60">
                    <Icon size={11} weight="duotone" className={step.tone} />
                    <div className="leading-tight">
                      <div className={`uppercase tracking-wider font-bold text-[9px] ${step.tone}`}>{step.label}</div>
                      <div className="text-[9px] text-zinc-500">{step.sub}</div>
                    </div>
                  </div>
                  {i < arr.length - 1 && <ArrowRight size={10} className="text-zinc-700" />}
                </div>
              );
            })}
          </div>
        </div>

        {/* Tabs */}
        <div className="px-6 border-b border-zinc-800 flex items-center gap-1">
          {[
            { key: "rules", label: `Rules (${rules.length})` },
            { key: "log", label: `Activity Log (${log.length})` },
          ].map((t) => (
            <button key={t.key} onClick={() => setActiveTab(t.key)}
              className={`px-3 py-2.5 text-xs font-medium border-b-2 transition-colors ${
                activeTab === t.key ? "border-teal-400 text-teal-300" : "border-transparent text-zinc-500 hover:text-zinc-300"
              }`}
              data-testid={`notif-tab-${t.key}`}>
              {t.label}
            </button>
          ))}
          <div className="flex-1" />
          {activeTab === "rules" && (
            <div className="flex items-center gap-2 py-1.5">
              {rules.length === 0 && (
                <Button onClick={seedDefaults} variant="outline" size="sm"
                  className="border-amber-700/60 text-amber-300 hover:bg-amber-500/10 text-xs h-7"
                  data-testid="seed-defaults-btn">
                  Seed 6 default rules
                </Button>
              )}
              <Button onClick={() => setEditing({ data: null })} size="sm"
                className="bg-teal-500 hover:bg-teal-600 text-zinc-950 text-xs h-7 font-semibold"
                data-testid="new-rule-btn">
                <Plus size={11} className="mr-1" /> New Rule
              </Button>
            </div>
          )}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {loading && rules.length === 0 ? (
            <p className="text-xs text-zinc-500 text-center mt-10">Loading…</p>
          ) : activeTab === "rules" ? (
            rules.length === 0 ? (
              <EmptyRules onSeed={seedDefaults} />
            ) : (
              <div className="space-y-4">
                <RuntimePreviewPanel rules={rules} />
                <div className="space-y-2">
                  {rules.map((r) => (
                    <RuleCard key={r.id} rule={r} onEdit={() => setEditing({ data: r })} onDelete={() => deleteRule(r.id, r.event_type)} />
                  ))}
                </div>
              </div>
            )
          ) : (
            <LogTable items={log} onAck={ackLog} />
          )}
        </div>

        {editing && enums && (
          <RuleEditor editing={editing} enums={enums} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />
        )}
      </div>

      <AlertFlowHelp open={helpOpen} onClose={() => setHelpOpen(false)} />
    </div>
  );
}

function EmptyRules({ onSeed }) {
  return (
    <div className="text-center py-16">
      <Bell size={32} className="mx-auto text-zinc-700 mb-2" weight="duotone" />
      <h3 className="text-sm font-semibold text-zinc-300 mb-1">No routing rules yet</h3>
      <p className="text-[11px] text-zinc-500 mb-4 max-w-md mx-auto">
        Seed the 6 operational defaults to start routing temperature excursions, seal breaches,
        reefer failures, delayed deliveries, and offline-device events to the right contacts.
      </p>
      <Button onClick={onSeed} className="bg-teal-500 hover:bg-teal-600 text-zinc-950 text-xs h-8 font-semibold">
        Seed default rules
      </Button>
    </div>
  );
}

function RuleCard({ rule, onEdit, onDelete }) {
  return (
    <Card className="bg-zinc-900/40 border-zinc-800 px-4 py-3 group" data-testid={`rule-card-${rule.event_type}`}>
      <div className="flex items-start gap-3">
        <div className="flex-1 min-w-0 space-y-2">
          {/* Header: event title + severity + disabled */}
          <div className="flex items-baseline gap-2 flex-wrap">
            <span className="text-sm font-semibold text-zinc-100">{EVENT_LABELS[rule.event_type] || rule.event_type}</span>
            <span className={`text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded border ${SEVERITY_COLORS[rule.severity_min] || ""}`}>
              ≥ {rule.severity_min}
            </span>
            {!rule.enabled && <span className="text-[9px] uppercase text-zinc-500">disabled</span>}
            {rule.description && (
              <span className="text-[10px] italic text-zinc-500 truncate" title={rule.description}>· {rule.description}</span>
            )}
          </div>

          {/* Four numbered sections, visually separated */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-2">
            {/* 1. Trigger */}
            <RuleSection num={1} label="Trigger" tone="text-amber-300">
              <div className="text-[10px] text-zinc-400 flex items-center gap-1">
                <Lightning size={9} weight="duotone" className="text-amber-300" />
                {EVENT_LABELS[rule.event_type] || rule.event_type}
              </div>
              <div className="text-[10px] text-zinc-500 mt-0.5">severity ≥ {rule.severity_min}</div>
            </RuleSection>

            {/* 2. Shipment Roles (resolved at runtime) */}
            <RuleSection num={2} label="Shipment Roles" tone="text-sky-300"
              sub="resolved per-shipment">
              <div className="flex flex-wrap gap-1">
                {rule.route_to.map((r, i) => (
                  <span key={i}
                    className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-sky-500/10 text-sky-300 text-[10px] border border-sky-700/40"
                    title="Resolved from the shipment's Operational Parties at runtime">
                    <UsersThree size={9} weight="duotone" />
                    {ROLE_LABELS[r] || r}
                  </span>
                ))}
              </div>
            </RuleSection>

            {/* 3. Delivery Channels */}
            <RuleSection num={3} label="Delivery" tone="text-violet-300">
              <div className="flex flex-wrap gap-1">
                {(rule.channels || []).map((c, i) => (
                  <span key={i}
                    className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-violet-500/10 text-violet-300 text-[10px] border border-violet-700/40">
                    <Broadcast size={9} weight="duotone" />
                    {CHANNEL_LABELS[c] || c}
                  </span>
                ))}
              </div>
              <div className="text-[10px] text-zinc-500 mt-0.5">
                {rule.acknowledge_required === false ? "no ack required" : "ack required"}
              </div>
            </RuleSection>

            {/* 4. SLA / Escalation */}
            <RuleSection num={4} label="SLA / Escalation" tone="text-red-300">
              <div className="text-[10px] text-zinc-400 flex items-center gap-1">
                <Clock size={9} weight="duotone" className="text-red-300" />
                ack window {rule.ack_window_minutes}m
              </div>
              {rule.escalation?.length > 0 ? (
                <div className="text-[9px] text-zinc-500 mt-0.5 space-y-0.5">
                  {rule.escalation.map((s, i) => (
                    <div key={i}>
                      <span className="text-zinc-600 font-mono">+{s.after_minutes}m</span> →{" "}
                      {s.route_to.map((r) => ROLE_LABELS[r] || r).join(", ")}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-[9px] text-zinc-600 mt-0.5 italic">no escalation steps</div>
              )}
            </RuleSection>
          </div>
        </div>

        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 shrink-0">
          <Button variant="ghost" size="sm" onClick={onEdit} className="h-7 w-7 p-0 text-zinc-400 hover:text-teal-300"
            data-testid={`edit-rule-${rule.event_type}`}>
            <PencilSimple size={11} />
          </Button>
          <Button variant="ghost" size="sm" onClick={onDelete} className="h-7 w-7 p-0 text-zinc-400 hover:text-red-300">
            <TrashSimple size={11} />
          </Button>
        </div>
      </div>
    </Card>
  );
}

function RuleSection({ num, label, tone, sub, children }) {
  return (
    <div className="p-2 rounded border border-zinc-800 bg-zinc-950/50">
      <div className="flex items-center gap-1 mb-1">
        <span className={`text-[9px] font-mono font-bold w-4 h-4 rounded-full bg-zinc-900 border border-zinc-800 flex items-center justify-center ${tone}`}>
          {num}
        </span>
        <span className={`text-[9px] uppercase tracking-wider font-bold ${tone}`}>{label}</span>
        {sub && <span className="text-[8px] uppercase tracking-wider text-zinc-600 ml-auto">{sub}</span>}
      </div>
      {children}
    </div>
  );
}

// ---------- Runtime preview panel ----------
function RuntimePreviewPanel({ rules }) {
  const [shipments, setShipments] = useState([]);
  const [shipmentId, setShipmentId] = useState("");
  const [ruleId, setRuleId] = useState("");
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/notification-rules/preview/shipments?limit=20`);
        setShipments(data.items || []);
        if (data.items?.[0]) setShipmentId(data.items[0].id);
      } catch { /* noop */ }
    })();
  }, []);

  useEffect(() => {
    if (!rules.length) return;
    // Default to temperature_excursion rule when present
    const def = rules.find((r) => r.event_type === "temperature_excursion") || rules[0];
    setRuleId(def.id);
  }, [rules]);

  useEffect(() => {
    if (!ruleId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const url = `${API}/notification-rules/${ruleId}/preview` + (shipmentId ? `?shipment_id=${shipmentId}` : "");
        const { data } = await axios.post(url);
        if (!cancelled) setPreview(data);
      } catch { /* noop */ }
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [ruleId, shipmentId]);

  if (!open) {
    return (
      <Button variant="outline" size="sm" onClick={() => setOpen(true)}
        className="border-teal-700/60 text-teal-300 hover:bg-teal-500/10 text-xs h-7"
        data-testid="open-runtime-preview">
        <Eye size={11} className="mr-1" /> Show Runtime Preview
      </Button>
    );
  }

  return (
    <Card className="bg-gradient-to-br from-teal-950/30 to-zinc-900/30 border-teal-900/50 p-4"
      data-testid="runtime-preview-panel">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Eye size={14} weight="duotone" className="text-teal-300" />
          <h3 className="text-sm font-semibold text-zinc-100">Runtime Preview</h3>
          <span className="text-[10px] text-zinc-500">Dry-run: which contacts would this rule reach on a specific shipment?</span>
        </div>
        <div className="flex items-center gap-2">
          <TestFireButton ruleId={ruleId} shipmentId={shipmentId} preview={preview} />
          <Button variant="ghost" size="sm" onClick={() => setOpen(false)} className="h-6 w-6 p-0 text-zinc-500">
            <X size={11} />
          </Button>
        </div>
      </div>

      <div className="flex items-center gap-2 mb-3 flex-wrap">
        <span className="text-[10px] uppercase tracking-wider text-zinc-500">Simulate event</span>
        <select value={ruleId} onChange={(e) => setRuleId(e.target.value)}
          className="bg-zinc-900 border border-zinc-800 rounded text-xs text-zinc-200 px-2.5 h-8 leading-tight"
          data-testid="preview-rule-select">
          {rules.map((r) => (
            <option key={r.id} value={r.id}>{EVENT_LABELS[r.event_type] || r.event_type}</option>
          ))}
        </select>
        <span className="text-[10px] uppercase tracking-wider text-zinc-500 ml-1">on shipment</span>
        <select value={shipmentId} onChange={(e) => setShipmentId(e.target.value)}
          className="bg-zinc-900 border border-zinc-800 rounded text-xs text-zinc-200 px-2.5 h-8 leading-tight max-w-xs"
          data-testid="preview-shipment-select">
          <option value="">(any shipment / tenant-wide fallback)</option>
          {shipments.map((s) => (
            <option key={s.id} value={s.id}>
              {(s.name || s.id.slice(0, 8))}{s.parties?.length ? ` · ${s.parties.length} parties` : " · 0 parties"}
            </option>
          ))}
        </select>
        {loading && <ArrowsClockwise size={12} className="animate-spin text-zinc-500" />}
      </div>

      {preview && (
        <div className="space-y-3" data-testid="preview-resolution">
          <PreviewResolutionRow
            stepLabel="Primary"
            stepIndex={0}
            afterMin={null}
            shipmentRoles={preview.primary?.shipment_roles || []}
            channels={preview.channels}
            recipients={preview.primary?.resolved_recipients || []}
          />
          {(preview.escalation || []).map((esc) => (
            <PreviewResolutionRow
              key={esc.step_index}
              stepLabel={`Escalation #${esc.step_index}`}
              stepIndex={esc.step_index}
              afterMin={esc.after_minutes}
              shipmentRoles={esc.shipment_roles}
              channels={preview.channels}
              recipients={esc.resolved_recipients}
            />
          ))}
          {(preview.primary?.resolved_recipients?.length || 0) === 0 && (
            <p className="text-[10px] italic text-amber-400 mt-1" data-testid="preview-no-recipients">
              ⚠ No contacts resolved for the primary roles on this shipment. If the event fires now,
              the SLA monitor will cascade to the next escalation step automatically.
            </p>
          )}
        </div>
      )}
    </Card>
  );
}

function TestFireButton({ ruleId, shipmentId, preview }) {
  const [showConfirm, setShowConfirm] = useState(false);
  const [chosenSeverity, setChosenSeverity] = useState("");
  const [firing, setFiring] = useState(false);
  const [lastFiredAt, setLastFiredAt] = useState(0);
  const [debounceLeft, setDebounceLeft] = useState(0);

  // 30s client-side debounce cooldown tick
  useEffect(() => {
    if (!lastFiredAt) return;
    const id = setInterval(() => {
      const left = Math.max(0, 30 - Math.floor((Date.now() - lastFiredAt) / 1000));
      setDebounceLeft(left);
      if (left === 0) clearInterval(id);
    }, 1000);
    return () => clearInterval(id);
  }, [lastFiredAt]);

  const doFire = async () => {
    setFiring(true);
    const t = toast.loading("Firing test event…");
    try {
      const params = new URLSearchParams();
      if (shipmentId) params.set("shipment_id", shipmentId);
      if (chosenSeverity) params.set("severity", chosenSeverity);
      const url = `${API}/notification-rules/${ruleId}/test-fire${params.toString() ? "?" + params.toString() : ""}`;
      const { data } = await axios.post(url);
      toast.success(
        `Test alert ${data.alert_id?.slice(0, 8)} created · ${data.resolved_recipients_count} recipient(s) · ${(data.channels_dispatched || []).join(", ") || "no channels"}`,
        { id: t, duration: 6000 },
      );
      setLastFiredAt(Date.now());
      setShowConfirm(false);
    } catch (e) {
      if (e.response?.status === 429) {
        toast.error(e.response.data?.detail || "Test fire debounced — wait a moment", { id: t });
        setLastFiredAt(Date.now());
      } else if (e.response?.status === 403) {
        toast.error("Admin role required to fire test events", { id: t });
      } else {
        toast.error(e.response?.data?.detail || "Test fire failed", { id: t });
      }
    } finally {
      setFiring(false);
    }
  };

  const disabled = !ruleId || firing || debounceLeft > 0;
  const recipientsCount = preview?.primary?.resolved_recipients?.length || 0;

  return (
    <>
      <Button
        variant="outline" size="sm"
        onClick={() => setShowConfirm(true)}
        disabled={disabled}
        className="border-amber-700/60 text-amber-300 hover:bg-amber-500/10 text-xs h-7"
        data-testid="test-fire-btn"
        title={debounceLeft > 0 ? `Cooldown ${debounceLeft}s` : "Send a labeled test event through this rule"}>
        <PaperPlaneTilt size={11} className="mr-1" weight="duotone" />
        Test Fire
        {debounceLeft > 0 && <span className="ml-1 font-mono text-[10px]">({debounceLeft}s)</span>}
      </Button>

      {showConfirm && (
        <div className="fixed inset-0 bg-black/70 z-[1950] flex items-center justify-center p-4"
          onClick={() => !firing && setShowConfirm(false)}
          data-testid="test-fire-confirm">
          <Card className="bg-zinc-950 border-amber-900/50 max-w-lg w-full p-5"
            onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start gap-3 mb-4">
              <PaperPlaneTilt size={20} weight="duotone" className="text-amber-300 shrink-0 mt-0.5" />
              <div>
                <h3 className="text-base font-semibold text-zinc-100">Send a test event?</h3>
                <p className="text-[11px] text-zinc-400 mt-1 leading-relaxed">
                  This creates a <span className="text-amber-300">real alert</span> in Alert Center and may
                  dispatch configured channels (email/SMS/Slack will log as
                  <span className="font-mono text-zinc-300"> mock_dispatched</span> when no provider is wired).
                  The alert is tagged <span className="font-mono text-zinc-300">is_test=true</span> and can be
                  bulk-cleared from Alert Center later.
                </p>
              </div>
            </div>

            {/* What will happen */}
            <div className="bg-zinc-900/50 border border-zinc-800 rounded p-3 mb-4 space-y-1.5 text-[11px]">
              <div><span className="text-zinc-500">Event:</span> <span className="text-zinc-200">{EVENT_LABELS[preview?.event_type] || preview?.event_type}</span></div>
              <div><span className="text-zinc-500">Shipment:</span> <span className="text-zinc-200">{preview?.shipment?.name || "(none — tenant-wide fallback)"}</span></div>
              <div><span className="text-zinc-500">Will route to:</span>{" "}
                <span className="text-zinc-200">{recipientsCount} contact{recipientsCount === 1 ? "" : "s"}</span>
                {preview?.primary?.resolved_recipients?.slice(0, 3).map((r, i) => (
                  <span key={i} className="text-zinc-400">{i === 0 ? " — " : ", "}{r.contact_name || r.role}</span>
                ))}
                {recipientsCount > 3 && <span className="text-zinc-500"> +{recipientsCount - 3} more</span>}
              </div>
              <div><span className="text-zinc-500">Channels:</span>{" "}
                {(preview?.channels || []).map((c) => (
                  <span key={c} className="inline-block px-1.5 rounded bg-violet-500/10 text-violet-300 text-[10px] border border-violet-700/40 mr-1">{CHANNEL_LABELS[c] || c}</span>
                ))}
              </div>
            </div>

            {/* Severity selector */}
            <div className="mb-4">
              <label className="text-[10px] uppercase tracking-wider text-zinc-500 block mb-1.5">
                Severity (defaults to rule's minimum)
              </label>
              <select value={chosenSeverity} onChange={(e) => setChosenSeverity(e.target.value)}
                className="w-full bg-zinc-900 border border-zinc-800 rounded text-xs text-zinc-200 px-2.5 h-9 leading-tight"
                data-testid="test-fire-severity">
                <option value="">Default ({preview?.severity_min || "warning"}) — recommended for testing</option>
                <option value="info">info (low) — won't trigger if rule severity_min &gt; info</option>
                <option value="warning">warning</option>
                <option value="critical">critical — DO NOT use unless you're testing escalation paths</option>
              </select>
              {chosenSeverity === "critical" && (
                <p className="text-[10px] text-red-400 mt-1 italic">
                  ⚠ Critical severity creates a high-priority alert. Use only when you're intentionally
                  testing the full escalation chain.
                </p>
              )}
            </div>

            <div className="flex items-center justify-end gap-2">
              <Button variant="ghost" size="sm" onClick={() => setShowConfirm(false)} disabled={firing}
                className="text-zinc-400 hover:text-zinc-200 text-xs h-8">
                Cancel
              </Button>
              <Button onClick={doFire} disabled={firing}
                className="bg-amber-500 hover:bg-amber-600 text-zinc-950 text-xs h-8 font-semibold"
                data-testid="test-fire-confirm-btn">
                <PaperPlaneTilt size={11} className="mr-1" weight="duotone" />
                {firing ? "Firing…" : "Send Test Event"}
              </Button>
            </div>
          </Card>
        </div>
      )}
    </>
  );
}

function PreviewResolutionRow({ stepLabel, stepIndex, afterMin, shipmentRoles, channels, recipients }) {
  const tone = stepIndex === 0 ? "text-teal-300 border-teal-700/40 bg-teal-500/5" : "text-amber-300 border-amber-700/40 bg-amber-500/5";
  return (
    <div className={`p-2 rounded border ${tone}`}>
      <div className="flex items-center gap-2 mb-1 flex-wrap">
        <span className={`text-[9px] uppercase font-bold tracking-wider px-1.5 py-0.5 rounded ${stepIndex === 0 ? "bg-teal-500/20 text-teal-200" : "bg-amber-500/20 text-amber-200"}`}>
          {stepLabel}
        </span>
        {afterMin != null && (
          <span className="text-[10px] font-mono text-zinc-400">after +{afterMin}m</span>
        )}
        <div className="flex items-center gap-1 ml-auto">
          {(shipmentRoles || []).map((r, i) => (
            <span key={i} className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-sky-500/10 text-sky-300 border border-sky-700/40">
              {ROLE_LABELS[r] || r}
            </span>
          ))}
        </div>
      </div>
      {recipients.length > 0 ? (
        <div className="space-y-1">
          {recipients.map((r, i) => (
            <div key={i} className="flex items-center gap-2 text-[11px] pl-2 flex-wrap"
              data-testid={`preview-recipient-${stepIndex}-${i}`}>
              <ArrowRight size={10} className="text-zinc-600" />
              <span className="text-zinc-100">{r.contact_name || "(unnamed)"}</span>
              <span className="text-zinc-500 text-[10px]">
                · {ROLE_LABELS[r.role] || r.role}
                {r.email ? ` · ${r.email}` : ""}
                {r.facility_name ? ` · ${r.facility_name}` : ""}
              </span>
              <ResolutionBadge resolution={r.resolution} />
              {channels?.map((c) => (
                <span key={c} className="text-[9px] uppercase tracking-wider px-1 rounded bg-violet-500/10 text-violet-300 border border-violet-700/40">
                  {CHANNEL_LABELS[c] || c}
                </span>
              ))}
            </div>
          ))}
        </div>
      ) : (
        <p className="text-[10px] italic text-zinc-500 pl-2">
          No contacts registered for {(shipmentRoles || []).join(", ") || "this step"} on this shipment.
        </p>
      )}
    </div>
  );
}

/**
 * Per-recipient resolution provenance badge. Tells the operator WHICH precedence rung
 * matched the contact so cross-org leakage from registry fallback can never be invisible.
 */
const RESOLUTION_LABEL = {
  shipment_role:      { txt: "shipment role",      tone: "bg-emerald-500/15 text-emerald-300 border-emerald-700/40", title: "Matched directly on the shipment's assigned parties." },
  shipment_intrinsic: { txt: "intrinsic match",    tone: "bg-teal-500/15 text-teal-300 border-teal-700/40",         title: "Contact is attached to the shipment under another shipment-role but their registry role matches." },
  registry_scoped:    { txt: "scoped registry",    tone: "bg-sky-500/15 text-sky-300 border-sky-700/40",            title: "Pulled from the org/facility registry, scoped to this shipment's organization/facility." },
  registry_fallback:  { txt: "registry fallback",  tone: "bg-amber-500/15 text-amber-300 border-amber-700/40",      title: "WARNING: No contact for this role on this shipment. Falling back to the first matching contact tenant-wide. Consider attaching the right party to this shipment." },
};

function ResolutionBadge({ resolution }) {
  if (!resolution) return null;
  const cfg = RESOLUTION_LABEL[resolution] || { txt: resolution, tone: "bg-zinc-500/15 text-zinc-400 border-zinc-700/40", title: "" };
  return (
    <span
      className={`text-[9px] uppercase tracking-wider font-bold px-1 py-0.5 rounded border ${cfg.tone}`}
      title={cfg.title}
      data-testid={`resolution-badge-${resolution}`}
    >
      {cfg.txt}
    </span>
  );
}

function LogTable({ items, onAck }) {
  if (items.length === 0) {
    return (
      <p className="text-[11px] text-zinc-500 text-center py-10 italic">
        No notification routing activity yet. As alerts fire and match rules, they will appear here.
      </p>
    );
  }
  const statusDot = {
    scheduled: "bg-zinc-500",
    sent: "bg-teal-400",
    acknowledged: "bg-emerald-400",
    expired: "bg-red-500",
  };
  return (
    <div className="space-y-1.5">
      {items.map((e) => (
        <div key={e.id} className="p-3 rounded border border-zinc-800 bg-zinc-900/30 group">
          <div className="flex items-baseline justify-between gap-2">
            <div className="flex items-center gap-2">
              <span className={`w-1.5 h-1.5 rounded-full ${statusDot[e.status] || "bg-zinc-500"}`}></span>
              <span className="text-[9px] uppercase tracking-wider text-zinc-500 font-semibold">
                {EVENT_LABELS[e.event_type] || e.event_type}
              </span>
              <span className={`text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded ${SEVERITY_COLORS[e.severity] || ""}`}>
                {e.severity}
              </span>
              {e.escalation_step > 0 && (
                <span className="text-[9px] uppercase text-amber-400 font-semibold">esc {e.escalation_step}</span>
              )}
            </div>
            <span className="text-[10px] font-mono text-zinc-500">{e.created_at?.slice(0, 16).replace("T", " ")}</span>
          </div>
          <div className="text-sm font-medium text-zinc-100 mt-1">
            {e.contact_name || "—"}
            <span className="text-[11px] text-zinc-500 font-normal ml-2">
              ({ROLE_LABELS[e.role] || e.role})
              {e.contact_email && ` · ${e.contact_email}`}
            </span>
          </div>
          {(e.shipment_name || e.facility_name) && (
            <div className="text-[10px] text-zinc-500">
              {e.shipment_name && <>shipment: <span className="text-zinc-400">{e.shipment_name}</span></>}
              {e.shipment_name && e.facility_name && " · "}
              {e.facility_name && <>facility: <span className="text-zinc-400">{e.facility_name}</span></>}
            </div>
          )}
          <div className="flex items-center justify-between mt-1">
            <span className="text-[10px] font-mono text-zinc-600 flex items-center gap-1.5">
              <Clock size={9} /> expires {e.expires_at?.slice(11, 16)} UTC
              {e.status === "acknowledged" && e.ack_by && (
                <span className="text-emerald-400 ml-2">ack by {e.ack_by}</span>
              )}
            </span>
            {(e.status === "scheduled" || e.status === "sent") && (
              <Button onClick={() => onAck(e.id)} size="sm" variant="outline"
                className="border-emerald-700/60 text-emerald-300 hover:bg-emerald-500/10 text-[10px] h-6 px-2 opacity-0 group-hover:opacity-100"
                data-testid={`ack-log-${e.id}`}>
                <CheckCircle size={10} className="mr-1" /> Acknowledge
              </Button>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

function RuleEditor({ editing, enums, onClose, onSaved }) {
  const { data } = editing;
  const isEdit = !!data?.id;
  const [form, setForm] = useState(() => data || {
    event_type: "temperature_excursion",
    severity_min: "warning",
    route_to: ["qa_manager"],
    channels: ["in_app"],
    escalation: [],
    ack_window_minutes: 30,
    acknowledge_required: true,
    enabled: true,
    description: "",
  });
  const [busy, setBusy] = useState(false);

  const toggleChannel = (ch) => {
    const has = (form.channels || []).includes(ch);
    setForm({
      ...form,
      channels: has ? form.channels.filter((c) => c !== ch) : [...(form.channels || []), ch],
    });
  };

  const save = async () => {
    setBusy(true);
    try {
      const url = `${API}/notification-rules${isEdit ? `/${data.id}` : ""}`;
      if (isEdit) await axios.put(url, form);
      else await axios.post(url, form);
      toast.success(`Rule ${isEdit ? "updated" : "created"}`);
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  const toggleRole = (role) => {
    setForm({
      ...form,
      route_to: form.route_to.includes(role)
        ? form.route_to.filter((r) => r !== role)
        : [...form.route_to, role],
    });
  };

  const addEscalation = () => {
    setForm({
      ...form,
      escalation: [...(form.escalation || []), { after_minutes: 30, route_to: [] }],
    });
  };

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-[1950]" onClick={onClose}>
      <Card className="bg-zinc-950 border-zinc-800 p-6 w-[640px] max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}
        data-testid="rule-editor">
        <h3 className="text-sm font-semibold text-zinc-100 mb-1">{isEdit ? "Edit" : "New"} Routing Rule</h3>
        <p className="text-[11px] text-zinc-500 mb-4">
          Maps {`{event, severity}`} → shipment-party roles → resolved contacts on the shipment.
        </p>

        <div className="space-y-3">
          <Field label="Event Type">
            <select value={form.event_type} onChange={(e) => setForm({ ...form, event_type: e.target.value })}
              className="w-full bg-zinc-900 border border-zinc-800 rounded text-sm text-zinc-100 px-2.5 h-10 leading-tight">
              {enums.event_types.map((ev) => (
                <option key={ev} value={ev}>{EVENT_LABELS[ev] || ev}</option>
              ))}
            </select>
          </Field>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Severity Minimum">
              <select value={form.severity_min} onChange={(e) => setForm({ ...form, severity_min: e.target.value })}
                className="w-full bg-zinc-900 border border-zinc-800 rounded text-sm text-zinc-100 px-2.5 h-10 leading-tight">
                {enums.severities.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </Field>
            <Field label="Acknowledgment Window (min)">
              <Input type="number" min={1} max={1440} value={form.ack_window_minutes}
                onChange={(e) => setForm({ ...form, ack_window_minutes: parseInt(e.target.value) || 30 })}
                className="bg-zinc-900 border-zinc-800 text-sm h-10 font-mono" />
            </Field>
          </div>

          <Field label="Shipment Roles (resolved at runtime)">
            <p className="text-[10px] text-zinc-500 -mt-1 mb-1.5 leading-snug">
              Select the roles on the affected shipment. When the event fires, the orchestrator
              looks up the contacts assigned to these roles on that specific shipment.
            </p>
            <div className="flex flex-wrap gap-1.5">
              {enums.shipment_party_roles.map((r) => (
                <button key={r} onClick={() => toggleRole(r)} type="button"
                  className={`text-[10px] px-2 py-1 rounded border transition-colors ${
                    form.route_to.includes(r)
                      ? "bg-sky-500/15 border-sky-700/60 text-sky-300"
                      : "bg-zinc-900 border-zinc-800 text-zinc-500 hover:text-zinc-300"
                  }`}>
                  {ROLE_LABELS[r] || r}
                </button>
              ))}
            </div>
          </Field>

          <Field label="Delivery Channels">
            <div className="flex flex-wrap gap-1.5">
              {(enums.channels || ["in_app", "email", "sms", "slack"]).map((ch) => {
                const active = (form.channels || []).includes(ch);
                return (
                  <button key={ch} onClick={() => toggleChannel(ch)} type="button"
                    className={`text-[10px] px-2 py-1 rounded border transition-colors ${
                      active
                        ? "bg-amber-500/15 border-amber-700/60 text-amber-300"
                        : "bg-zinc-900 border-zinc-800 text-zinc-500 hover:text-zinc-300"
                    }`}
                    data-testid={`channel-toggle-${ch}`}>
                    {CHANNEL_LABELS[ch] || ch}
                  </button>
                );
              })}
            </div>
            <p className="text-[10px] text-zinc-500 mt-1">
              In-app is delivered instantly. Email/SMS/Slack rows are logged for audit; real dispatch requires provider API keys (Resend, Twilio, Slack webhook).
            </p>
          </Field>

          <Field label="Acknowledgment">
            <label className="flex items-center gap-2 text-xs text-zinc-300 cursor-pointer">
              <input type="checkbox"
                checked={!!form.acknowledge_required}
                onChange={(e) => setForm({ ...form, acknowledge_required: e.target.checked })}
                className="accent-teal-500"
                data-testid="ack-required-toggle" />
              Require explicit acknowledgement before escalation/auto-resolve
            </label>
          </Field>

          <Field label="Escalation Steps">
            <div className="space-y-2">
              {(form.escalation || []).map((step, i) => (
                <div key={i} className="p-2.5 rounded border border-zinc-800 bg-zinc-900/40">
                  <div className="flex items-center gap-2 mb-2">
                    <Input type="number" min={1} value={step.after_minutes}
                      onChange={(e) => {
                        const next = [...form.escalation];
                        next[i] = { ...next[i], after_minutes: parseInt(e.target.value) || 30 };
                        setForm({ ...form, escalation: next });
                      }}
                      className="bg-zinc-900 border-zinc-800 text-xs h-7 w-20 font-mono" />
                    <span className="text-[10px] text-zinc-400">minutes after primary →</span>
                    <Button variant="ghost" size="sm" onClick={() => setForm({ ...form, escalation: form.escalation.filter((_, j) => j !== i) })}
                      className="h-6 w-6 p-0 text-zinc-500 hover:text-red-300 ml-auto">
                      <TrashSimple size={10} />
                    </Button>
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {enums.shipment_party_roles.map((r) => (
                      <button key={r} type="button"
                        onClick={() => {
                          const next = [...form.escalation];
                          const cur = next[i].route_to || [];
                          next[i] = { ...next[i], route_to: cur.includes(r) ? cur.filter((x) => x !== r) : [...cur, r] };
                          setForm({ ...form, escalation: next });
                        }}
                        className={`text-[10px] px-2 py-0.5 rounded border ${
                          (step.route_to || []).includes(r)
                            ? "bg-amber-500/15 border-amber-700/60 text-amber-300"
                            : "bg-zinc-900 border-zinc-800 text-zinc-500"
                        }`}>
                        {ROLE_LABELS[r] || r}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
              <Button variant="outline" size="sm" onClick={addEscalation}
                className="border-zinc-700 text-zinc-300 text-xs h-7 w-full">
                <Plus size={10} className="mr-1" /> Add escalation step
              </Button>
            </div>
          </Field>

          <Field label="Description (optional)">
            <Input value={form.description || ""} onChange={(e) => setForm({ ...form, description: e.target.value })}
              className="bg-zinc-900 border-zinc-800 text-sm h-8"
              placeholder="e.g. Temperature excursion → QA manager (escalates after 15min)" />
          </Field>

          <div className="flex items-center gap-2 mt-2">
            <input type="checkbox" id="enabled" checked={!!form.enabled}
              onChange={(e) => setForm({ ...form, enabled: e.target.checked })}
              className="accent-teal-500" />
            <Label htmlFor="enabled" className="text-xs text-zinc-300 cursor-pointer">Enabled</Label>
          </div>
        </div>

        <div className="flex justify-end gap-2 mt-5">
          <Button variant="outline" onClick={onClose} className="border-zinc-700 text-xs h-8">Cancel</Button>
          <Button onClick={save} disabled={busy || form.route_to.length === 0}
            className="bg-teal-500 hover:bg-teal-600 text-zinc-950 text-xs h-8 font-semibold"
            data-testid="rule-save-btn">
            <PaperPlaneTilt size={11} className="mr-1" /> {isEdit ? "Save Changes" : "Create Rule"}
          </Button>
        </div>
      </Card>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-[10px] uppercase tracking-wider text-zinc-400 mb-1 block">{label}</Label>
      {children}
    </div>
  );
}
